import { useCallback, useEffect, useMemo, useState } from 'react';
import { IProductDelivery, IproductReferenceDataInCountry, IShipmentData } from 'types/orders';
import { getShipmentDetailedData } from 'services/orders';
import { getDestinationDisplayName, getShortCountryName } from '../../trackingOrder.utils';
import { groupProductsBySku } from '../../trackingOrder.utils';

export const NO_INFORMATION_LABEL = 'Sin información';

export interface IDestinationDisplay {
  title: string;
  countryFlagUrl: string;
  countryShortName: string;
}

const EMPTY_DESTINATION_DISPLAY: IDestinationDisplay = {
  title: '',
  countryFlagUrl: '',
  countryShortName: ''
};

interface IUseShipmentDetailDrawerParams {
  isOpen: boolean;
  orderId?: number | string;
  shipmentIds: number[];
  onClose: () => void;
}

export const useShipmentDetailDrawer = ({
  isOpen,
  orderId,
  shipmentIds,
  onClose
}: IUseShipmentDetailDrawerParams) => {
  const [shipmentDataList, setShipmentDataList] = useState<(IShipmentData | null)[]>([]);
  const [references, setReferences] = useState<IproductReferenceDataInCountry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchErrorsByIndex, setFetchErrorsByIndex] = useState<Record<number, boolean>>({});
  const [selectedProductForSerial, setSelectedProductForSerial] = useState<IProductDelivery | null>(null);
  const [productsForSerialModal, setProductsForSerialModal] = useState<IProductDelivery[]>([]);
  const [selectedShipmentIndexForSerial, setSelectedShipmentIndexForSerial] = useState<number>(0);

  const hasAnyError = Object.values(fetchErrorsByIndex).some(Boolean);
  const hasAllFailed = hasAnyError && !isLoading && shipmentDataList.every(shipmentData => shipmentData === null);

  const fetchShipmentDetail = useCallback(async () => {
    if (!shipmentIds.length) return;

    setIsLoading(true);
    setShipmentDataList([]);
    setFetchErrorsByIndex({});

    const results = await Promise.allSettled(
      shipmentIds.map(id => getShipmentDetailedData(id))
    );

    setShipmentDataList(
      results.map(r => (r.status === 'fulfilled' ? r.value : null))
    );

    setFetchErrorsByIndex(
      results.reduce<Record<number, boolean>>(
        (acc, r, i) => ({ ...acc, [i]: r.status === 'rejected' }),
        {}
      )
    );
    setIsLoading(false);
  }, [shipmentIds]);

  const retryShipmentAtIndex = useCallback(
    (index: number) => {
      const id = shipmentIds[index];

      if (id == null) return;

      getShipmentDetailedData(id)
        .then(data => {
          setShipmentDataList(prev => {
            const next = [...prev];
            next[index] = data;
            return next;
          });
          setFetchErrorsByIndex(prev => ({ ...prev, [index]: false }));
        })
        .catch(() => setFetchErrorsByIndex(prev => ({ ...prev, [index]: true })));
    },
    [shipmentIds]
  );

  const handleOverlayClick = useCallback(() => {
    if (!selectedProductForSerial) onClose();
  }, [onClose, selectedProductForSerial]);

  const handleOpenSerialsModal = useCallback(
    (product: IProductDelivery, productsFromShipment: IProductDelivery[], shipmentIndex: number) => {
      const groupedProducts = groupProductsBySku(productsFromShipment);
      const productSku = product?.sku || 'sin-sku';
      const sameSkuProducts = groupedProducts[productSku] || [product];

      setSelectedProductForSerial(product);
      setProductsForSerialModal(sameSkuProducts);
      setSelectedShipmentIndexForSerial(shipmentIndex);
    },
    []
  );

  const handleCloseSerialsModal = useCallback(() => {
    setSelectedProductForSerial(null);
    setProductsForSerialModal([]);
  }, []);

  const handleSerialsUpdated = useCallback(async () => {
    const id = shipmentIds[selectedShipmentIndexForSerial];

    if (id == null) return;

    try {
      const data = await getShipmentDetailedData(id);

      setShipmentDataList(prev => {
        const next = [...prev];
        next[selectedShipmentIndexForSerial] = data;
        return next;
      });
    } catch {
      setFetchErrorsByIndex(prev => ({ ...prev, [selectedShipmentIndexForSerial]: true }));
    }
  }, [shipmentIds, selectedShipmentIndexForSerial]);

  useEffect(() => {
    if (isOpen && orderId && shipmentIds.length) fetchShipmentDetail();
  }, [isOpen, orderId, shipmentIds, fetchShipmentDetail]);

  useEffect(() => {
    if (!isOpen) {
      setShipmentDataList([]);
      setReferences([]);
      setFetchErrorsByIndex({});
      setSelectedProductForSerial(null);
      setProductsForSerialModal([]);
    }
  }, [isOpen]);

  const firstValidShipment = useMemo(
    () => shipmentDataList.find(shipmentData => shipmentData != null) ?? null,
    [shipmentDataList]
  );

  const destinationDisplay = useMemo((): IDestinationDisplay => {
    if (!firstValidShipment) {
      return EMPTY_DESTINATION_DISPLAY;
    }

    return {
      title: getDestinationDisplayName(
        firstValidShipment.place,
        firstValidShipment.receiverName ?? firstValidShipment.receiverInfo?.receiverName,
        firstValidShipment.country?.name
      ),
      countryFlagUrl: firstValidShipment.country?.flag ?? '',
      countryShortName: getShortCountryName(firstValidShipment.country?.name) || ''
    };
  }, [firstValidShipment]);

  return {
    shipmentDataList,
    shipmentIds,
    isLoading,
    hasAnyError,
    hasAllFailed,
    fetchErrorsByIndex,
    firstValidShipment,
    destinationDisplay,
    references,
    selectedProductForSerial,
    productsForSerialModal,
    fetchShipmentDetail,
    retryShipmentAtIndex,
    handleOverlayClick,
    handleOpenSerialsModal,
    handleCloseSerialsModal,
    handleSerialsUpdated
  };
};
